import React, { useContext, useEffect, useMemo, useState } from "react";
import { AuthContext } from "../state/AuthContext";
import SpeechInput from "../components/SpeechInput";

const initialForm = {
  fullName: "",
  address: "",
  mobile: "",
  email: "",
  aadharNo: "",
  panNo: "",
  birthDate: "",
  workTypeId: "",
  workId: "",
  paymentAmount: "",
  vehicleNo: "",
  licenseType: "",
  previousDocumentNo: "",
};

export default function PublicEnquiryPage({
  apiPath = "/api/public/enquiries",
  title = "Customer Enquiry Form",
  description = "Fill your details and submit. You will receive an Application No.",
  submitLabel = "Submit Enquiry",
  showLeadFields = false,
}) {
  const { http, user } = useContext(AuthContext);
  const [form, setForm] = useState(initialForm);
  const [workTypes, setWorkTypes] = useState([]);
  const [works, setWorks] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!showLeadFields || !user) return;

    let alive = true;
    async function loadOptions() {
      setLoadingOptions(true);
      try {
        const [workTypeRes, workRes] = await Promise.all([
          http.get("/api/master/work-types"),
          http.get("/api/master/works"),
        ]);
        if (!alive) return;
        setWorkTypes(workTypeRes.data.items);
        setWorks(workRes.data.items);
      } catch (err) {
        if (!alive) return;
        setError(err?.response?.data?.message || "Failed to load work options.");
      } finally {
        if (alive) setLoadingOptions(false);
      }
    }

    loadOptions();
    return () => {
      alive = false;
    };
  }, [http, showLeadFields, user]);

  const selectedWorkType = workTypes.find((item) => String(item.id) === String(form.workTypeId));
  const filteredWorks = works.filter(
    (item) => String(item.workTypeId) === String(form.workTypeId)
  );
  const selectedWork = works.find((item) => String(item.id) === String(form.workId));

  const canSubmit = useMemo(() => {
    const hasBaseFields =
      form.fullName.trim() &&
      form.mobile.trim() &&
      form.address.trim() &&
      form.birthDate;

    if (!showLeadFields) {
      return hasBaseFields;
    }

    if (!form.workTypeId || !form.workId || form.paymentAmount === "") {
      return false;
    }

    if (selectedWorkType?.code === "vehicle" && !form.vehicleNo.trim()) {
      return false;
    }

    if (selectedWorkType?.code === "driving_licence" && !form.licenseType.trim()) {
      return false;
    }

    return hasBaseFields;
  }, [form, selectedWorkType, showLeadFields]);

  const onChange = (e) => {
    const { name, value } = e.target;
    const nextValue =
      name === "fullName" || name === "address"
        ? value.toUpperCase()
        : value;
    setForm((s) => {
      if (name === "workTypeId") {
        return {
          ...s,
          workTypeId: nextValue,
          workId: "",
          paymentAmount: "",
          vehicleNo: "",
          licenseType: "",
          previousDocumentNo: "",
        };
      }

      if (name === "workId") {
        const nextWork = works.find((item) => String(item.id) === String(nextValue));
        return {
          ...s,
          workId: nextValue,
          paymentAmount: nextWork ? String(nextWork.fee) : "",
        };
      }

      return { ...s, [name]: nextValue };
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setResult(null);
    try {
      const res = await http.post(apiPath, form);
      setResult(res.data);
      setForm(initialForm);
    } catch (err) {
      setError(
        err?.response?.data?.message || "Failed to submit enquiry. Try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="row justify-content-center">
      <div className="col-12 col-lg-8">
        <div className="card shadow-sm">
          <div className="card-body">
            <h5 className="card-title mb-1">{title}</h5>
            <p className="text-muted mb-4">{description}</p>

            {result ? (
              <div className="alert alert-success">
                <div className="fw-semibold">Enquiry submitted successfully.</div>
                <div className="mt-1">
                  Your Application No:{" "}
                  <span className="fw-bold">{result.applicationNo}</span>
                </div>
              </div>
            ) : null}

            {error ? <div className="alert alert-danger">{error}</div> : null}
            {loadingOptions ? (
              <div className="alert alert-secondary">Loading work details...</div>
            ) : null}

            <form onSubmit={onSubmit} className="row g-3">
              <div className="col-12">
                <label className="form-label">Name</label>
                <SpeechInput
                  as="input"
                  name="fullName"
                  value={form.fullName}
                  onChange={onChange}
                  inputClassName="text-uppercase"
                  placeholder="Full name"
                  required
                />
              </div>

              <div className="col-12">
                <label className="form-label">Address</label>
                <SpeechInput
                  as="textarea"
                  name="address"
                  value={form.address}
                  onChange={onChange}
                  inputClassName="text-uppercase"
                  rows={3}
                  placeholder="Address"
                  required
                />
              </div>

              <div className="col-12 col-md-6">
                <label className="form-label">Mobile No</label>
                <SpeechInput
                  as="input"
                  name="mobile"
                  value={form.mobile}
                  onChange={onChange}
                  inputMode="numeric"
                  placeholder="10 digit mobile no"
                  required
                />
              </div>

              <div className="col-12 col-md-6">
                <label className="form-label">E-Mail Id</label>
                <SpeechInput
                  as="input"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={onChange}
                  placeholder="name@example.com"
                />
              </div>

              <div className="col-12 col-md-6">
                <label className="form-label">Adhar Card No</label>
                <SpeechInput
                  as="input"
                  name="aadharNo"
                  value={form.aadharNo}
                  onChange={onChange}
                  placeholder="Aadhar"
                />
              </div>

              <div className="col-12 col-md-6">
                <label className="form-label">PAN No</label>
                <SpeechInput
                  as="input"
                  name="panNo"
                  value={form.panNo}
                  onChange={onChange}
                  placeholder="PAN"
                />
              </div>

              <div className="col-12 col-md-6">
                <label className="form-label">Birth Date</label>
                <input
                  name="birthDate"
                  value={form.birthDate}
                  onChange={onChange}
                  className="form-control"
                  type="date"
                  required
                />
              </div>

              {showLeadFields ? (
                <>
                  <div className="col-12 col-md-6">
                    <label className="form-label">Work Type</label>
                    <select
                      name="workTypeId"
                      value={form.workTypeId}
                      onChange={onChange}
                      className="form-select"
                      required
                    >
                      <option value="">Select Work Type</option>
                      {workTypes.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-12 col-md-6">
                    <label className="form-label">Work</label>
                    <select
                      name="workId"
                      value={form.workId}
                      onChange={onChange}
                      className="form-select"
                      required
                      disabled={!form.workTypeId}
                    >
                      <option value="">Select Work</option>
                      {filteredWorks.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-12 col-md-6">
                    <label className="form-label">Payment</label>
                    <SpeechInput
                      as="input"
                      name="paymentAmount"
                      value={form.paymentAmount}
                      onChange={onChange}
                      inputMode="decimal"
                      placeholder="Payment amount"
                      required
                    />
                    {selectedWork ? (
                      <div className="form-text">Default fee: {Number(selectedWork.fee).toFixed(2)}</div>
                    ) : null}
                  </div>

                  {selectedWorkType?.code === "vehicle" ? (
                    <div className="col-12 col-md-6">
                      <label className="form-label">Vehicle No</label>
                      <SpeechInput
                        as="input"
                        name="vehicleNo"
                        value={form.vehicleNo}
                        onChange={onChange}
                        placeholder="Vehicle number"
                        required
                      />
                    </div>
                  ) : null}

                  {selectedWorkType?.code === "driving_licence" ? (
                    <>
                      <div className="col-12 col-md-6">
                        <label className="form-label">License Type</label>
                        <SpeechInput
                          as="input"
                          name="licenseType"
                          value={form.licenseType}
                          onChange={onChange}
                          placeholder="License type"
                          required
                        />
                      </div>

                      <div className="col-12 col-md-6">
                        <label className="form-label">Previous Document No</label>
                        <SpeechInput
                          as="input"
                          name="previousDocumentNo"
                          value={form.previousDocumentNo}
                          onChange={onChange}
                          placeholder="Optional previous document number"
                        />
                      </div>
                    </>
                  ) : null}
                </>
              ) : null}

              <div className="col-12 d-flex justify-content-end">
                <button
                  type="submit"
                  disabled={!canSubmit || submitting}
                  className="btn btn-primary"
                >
                  {submitting ? "Submitting..." : submitLabel}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
